import queryString from 'query-string';
import { NavigateFunction } from 'react-router-dom';
import { IBokbiStatistics } from '../store/useBokbiHistory';

// 천원 단위로 콤마 찍어주는 함수
export function formatNumberWithComma(value: number): string {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// 상품명에서 대괄호 부분 제거하는 함수
export function removeBracketContent(productName: string): string {
  // 대괄호와 그 안의 내용을 제거하고 trim 처리
  return productName.replace(/\[.*?\]/g, '').trim();
}

/**
 * @description 파일 확장자가 accept 조건을 만족하는지 여부를 확인하는 함수
 * @param accept string - 허용하는 확장자 (예: "image/*", ".png, .jpg")
 * @param fileName string - 업로드된 파일의 이름
 * @returns boolean - accept에 포함되는 확장자면 true, 아니면 false
 */
export const isFileAccepted = (accept: string, fileName: string): boolean => {
  const fileExtension = fileName.split('.').pop()?.toLowerCase(); // 파일 확장자 추출
  const acceptArray = accept.split(',').map(ext => ext.trim().toLowerCase());

  if (!fileExtension) return false; // 확장자를 못 찾으면 false 반환

  return acceptArray.some(acc => {
    if (acc === '*/*') return true; // 모든 파일 허용
    if (acc === 'image/*')
      return fileExtension.match(/(jpg|jpeg|png|gif|bmp|svg|webp)/);
    if (acc === 'application/pdf') return fileExtension === 'pdf';
    if (acc === 'audio/*') return fileExtension.match(/(mp3|wav|ogg|aac|flac)/);
    if (acc === 'video/*') return fileExtension.match(/(mp4|avi|mkv|mov|wmv)/);
    return acc.startsWith('.') ? `.${fileExtension}` === acc : false;
  });
};

export function formatDate(inputDate: string | Date) {
  let date: Date;
  if (typeof inputDate === 'string') {
    // 입력값이 "yyyyMMdd" 형식인 경우 "-"를 추가하여 "yyyy-MM-dd" 형식으로 변환
    if (/^\d{8}$/.test(inputDate)) {
      inputDate = inputDate.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');
    }

    // 입력값이 "yyyy.MM.dd" 형식인 경우 "-"로 변환하고 0패딩 추가 (Safari용)
    if (/^\d{4}\.\d{1,2}\.\d{1,2}$/.test(inputDate)) {
      const [year, month, day] = inputDate.split('.');
      inputDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    // 입력값이 "yyyy.MM.dd HH:mm" 형식인 경우 (시간 포함, Safari용)
    if (/^\d{4}\.\d{1,2}\.\d{1,2} \d{1,2}:\d{1,2}$/.test(inputDate)) {
      const [datePart, timePart] = inputDate.split(' ');
      const [year, month, day] = datePart.split('.');
      const [hour, minute] = timePart.split(':');
      inputDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:00`;
    }

    // 시간 없는 날짜 형식에 기본 시간 추가 (Safari용)
    if (/^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12]\d|3[01])$/.test(inputDate)) {
      inputDate += 'T00:00:00';
    }

    // 문자열 형태의 날짜를 Date 객체로 변환
    date = new Date(inputDate);
  } else if (inputDate instanceof Date) {
    // 이미 Date 객체인 경우 그대로 사용
    date = inputDate;
  } else {
    throw new Error(
      'Invalid input. Input should be a string or a Date object.'
    );
  }

  const yyyyYear = date.getFullYear();
  const yyYear = yyyyYear.toString().slice(2);
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // 월은 0부터 시작하므로 +1 해줍니다.
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  const ampm = hours < 12 ? '오전' : '오후';
  // 12시간제: 0시는 12로, 13~23시는 hour-12, 12시는 12 유지
  const ampmhh = (hours % 12 === 0 ? 12 : hours % 12)
    .toString()
    .padStart(2, '0');
  const hh = hours.toString().padStart(2, '0');
  const mm = minutes.toString().padStart(2, '0');
  const ss = seconds.toString().padStart(2, '0'); // 초 추가

  // 로컬 시간 기반 ISO 8601 형식
  const localISOString = `${yyyyYear}-${month}-${day}T${hours
    .toString()
    .padStart(2, '0')}:${mm}:${ss}`;

  return {
    'yy.mm.dd': `${yyYear}.${month}.${day}`,
    'yyyy.mm.dd': `${yyyyYear}.${month}.${day}`,
    'yy-mm-dd': `${yyYear}-${month}-${day}`,
    'yyyy-mm-dd': `${yyyyYear}-${month}-${day}`,
    'yyyy.mm.dd hh:mm': `${yyyyYear}.${month}.${day} ${hh}:${mm}`,
    yyyymmddhhmmss: `${yyyyYear}${month}${day}${hh}${mm}${ss}`,
    'yyyy-mm-ddTHH:mm:ss': localISOString, // 로컬 시간 기반 ISO 8601 형식
    timestamp: `${yyyyYear}년 ${month}월 ${day}일 ${ampm} ${ampmhh}:${mm}`,
  };
}

/**
 * @description 날짜 문자열을 연, 월, 일, 시, 분으로 분리하여 반환하는 함수 (0 제거)
 * @param dateTimeStr - "YYYY.MM.DD HH:MM" 또는 "YYYY.MM.DD" 형식의 날짜 문자열
 * @returns { year: string, month: string, day: string, hour: string, minute: string }
 */
export const parseDateTime = (dateTimeStr: string) => {
  const [datePart, timePart] = dateTimeStr.split(' ');

  if (!datePart) {
    throw new Error(
      '올바른 형식의 날짜 문자열이 아닙니다. "YYYY.MM.DD HH:MM" 또는 "YYYY.MM.DD" 형식을 사용하세요.'
    );
  }

  // 각 부분을 숫자로 변환해 0 제거 후 다시 문자열로 변환
  const [year, month, day] = datePart
    .split('.')
    .map(part => String(parseInt(part, 10)));

  let hour = '';
  let minute = '';

  if (timePart) {
    [hour, minute] = timePart
      .split(':')
      .map(part => String(parseInt(part, 10)));
  }

  return { year, month, day, hour, minute };
};

export const getDiscountRate = (
  discount: string | number,
  original: string | number
) => {
  let discountNumber, originalNumber;

  if (typeof discount === 'string') {
    discountNumber = discount ? parseInt(discount.replaceAll(',', '')) : 0;
  } else {
    discountNumber = discount;
  }
  if (typeof original === 'string') {
    originalNumber = original ? parseInt(original.replaceAll(',', '')) : 0;
  } else {
    originalNumber = original;
  }

  if (!discountNumber || !originalNumber) return '';

  return Math.round((1 - discountNumber / originalNumber) * 100);
};

// 주문등록 할때 한국어 테이블 헤더를 영어로 바꾸기 위한 데이터
const orderFieldMapping: { [key: string]: any } = {
  순번: 'sequence',
  상품명: 'productCodeOrName',
  상품코드: 'productCodeOrName',
  연락처: 'telNo',
  이메일: 'email',
  이름1: 'name1',
  '양력/음력1': 'solarType1',
  생년1: 'year1',
  생월1: 'month1',
  생일1: 'day1',
  생시1: 'hour1',
  생분1: 'minute1',
  성별1: 'sex1',
  이름2: 'name2',
  '양력/음력2': 'solarType2',
  생년2: 'year2',
  생월2: 'month2',
  생일2: 'day2',
  생시2: 'hour2',
  생분2: 'minute2',
  성별2: 'sex2',
};

// Define the default fields with null values
const defaultFields: { [key: string]: any } = {
  sequence: null,
  productCodeOrName: null,
  telNo: null,
  email: null,
  name1: null,
  solarType1: null,
  year1: null,
  month1: null,
  day1: null,
  hour1: null,
  minute1: null,
  sex1: null,
  name2: null,
  solarType2: null,
  year2: null,
  month2: null,
  day2: null,
  hour2: null,
  minute2: null,
  sex2: null,
};

// Function to map Korean keys to English keys using orderFieldMapping
export const mapHeadersToEnglish = (data: Array<{ [key: string]: any }>) => {
  return data.map(item => {
    const mappedItem: { [key: string]: any } = { ...defaultFields }; // Initialize with default null values

    for (const key in item) {
      // Use the mapped key from orderFieldMapping, or fall back to the original key if not found
      const newKey = orderFieldMapping[key] || key;
      mappedItem[newKey] = item[key];
    }

    console.log(mappedItem);
    return mappedItem;
  });
};

// URL 업데이트
export const updateUrlWithParams = (
  params: Record<string, any>,
  navigate: NavigateFunction,
  options?: { replace?: boolean }
) => {
  const newQuery = queryString.stringify(params, { skipEmptyString: true });
  const shouldReplace = options?.replace ?? true;
  navigate({ search: `?${newQuery}` }, { replace: shouldReplace });
};

// 전화번호 포맷팅
export function formatPhoneNumber(phoneNumber: string): string {
  const formattedPhoneNumber = phoneNumber.replace(/\D/g, '');

  // 만약 숫자 길이가 11자리라면 010-XXXX-XXXX 형식으로 변환합니다.
  if (formattedPhoneNumber.length === 11) {
    return `${formattedPhoneNumber.slice(0, 3)}-${formattedPhoneNumber.slice(
      3,
      7
    )}-${formattedPhoneNumber.slice(7)}`;
  }

  return formattedPhoneNumber;
}

// 복비 통계 데이터 주간, 월간, 90일간, 연간으로 합산해주는 함수
export function aggregateDataByPeriod(
  data: IBokbiStatistics[],
  period: '주간' | '월간' | '90일간' | '연간' | string,
  referenceDate: Date = new Date()
): { name: string; value: number }[] {
  // 기간에 따른 일수 설정
  let days: number;
  switch (period) {
    case '주간':
      days = 7;
      break;
    case '월간':
      days = 30;
      break;
    case '90일간':
      days = 90;
      break;
    case '연간':
      days = 365;
      break;
    default:
      days = 7;
  }

  // 기준 날짜로부터 지정한 기간의 시작일 계산
  const startDate = new Date(referenceDate);
  startDate.setDate(startDate.getDate() - days + 1); // (+1을 해서 기준일 포함 여부는 필요에 따라 조정)

  // 날짜 범위 내의 데이터만 필터링 (createdAt이 startDate와 referenceDate 사이)
  const filteredData = data.filter(item => {
    const itemDate = new Date(item.createdAt);
    return itemDate >= startDate && itemDate <= referenceDate;
  });

  // 합산
  const totalCharge = filteredData.reduce(
    (sum, item) => sum + item.amount.CHARGE,
    0
  );
  const totalOrderPay = filteredData.reduce(
    (sum, item) => sum + item.amount.ORDER_PAY,
    0
  );

  return [
    { name: '충전', value: totalCharge },
    { name: '사용', value: totalOrderPay },
  ];
}


export type PeriodType = 'week' | 'month' | '90days' | 'year';

export function getStartDateByPeriod(
  period: PeriodType,
  referenceDate: Date = new Date(),
): Date {
  const date = new Date(referenceDate);
  switch (period) {
    case 'week':
      date.setDate(date.getDate() - 7);
      break;
    case 'month':
      date.setDate(date.getDate() - 30);
      break;
    case '90days':
      date.setDate(date.getDate() - 90);
      break;
    case 'year':
      date.setDate(date.getDate() - 365);
      break;
  }
  date.setHours(0, 0, 0, 0);
  return date;
}

export function normalizePhoneNumber(phone: string): string {
  // 숫자 이외의 문자 제거
  let digits = phone.replace(/\D/g, '');

  // +82 등 국가번호가 포함된 경우: 앞의 '82'를 제거하고 0을 붙임
  if (digits.startsWith('82')) {
    digits = '0' + digits.substring(2);
  }

  // 만약 10자리인데 맨 앞이 0이 아니라면, 0을 추가 (예: 1012345678 -> 01012345678)
  if (digits.length === 10 && digits[0] !== '0') {
    digits = '0' + digits;
  }

  // 11자리인 경우: 3-4-4 형식 (예: 010-1234-5678)
  if (digits.length === 11) {
    return `${digits.substring(0, 3)}-${digits.substring(
      3,
      7
    )}-${digits.substring(7)}`;
  }
  // 10자리인 경우: 3-3-4 형식 (예: 010-123-4567)
  else if (digits.length === 10) {
    return `${digits.substring(0, 3)}-${digits.substring(
      3,
      6
    )}-${digits.substring(6)}`;
  }

  // 예상한 길이가 아니라면 원본 문자열 반환
  return phone;
}
